import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { AlertCircle, Loader2, Users, Wand2, X } from 'lucide-react';
import type {
  ProposalNodeSet,
  RfqProject,
  SupplierOrg,
  TimberRfqDrynessSpec,
  TimberSupplierInvite,
  TimberSupplierStructureRef,
} from '../../types';
import { db } from '../../utils/firebase';
import SupplierShipToFields, {
  defaultShipToConfigForOrg,
  initialShipToConfigForOrg,
  type ProjectSiteAddressInput,
} from './SupplierShipToFields';
import type { SupplierShipToConfig } from '../../utils/supplierShipTo';
import { subscribeToTimberItems, type TimberItem } from '../../utils/materialsDatabaseService';
import { speciesProvidedBySupplier } from '../../utils/timberSupplierSpecies';
import type { SelectableTimberRfqDrynessSpec } from '../../utils/supplierTimberExport';
import {
  allSelectableTimberSpecies,
  buildTimberSupplierInvites,
  rfqProjectTimberSpeciesDryness,
  rfqProjectTimberSpeciesDrynessEntries,
  rfqProjectTimberSupplierSpecies,
} from '../../utils/timberRfqSuppliers';
import {
  proposalMixedDrynessSpecies,
  proposalSpeciesDrynessForInvite,
  type ProposalSpeciesSelectionSource,
} from '../../utils/timberRfqStructureScope';
import { useCRM } from '../../utils/CRMContext';
import { normalizeNotifyEmails, supplierRfqRecipientCandidates } from '../../utils/supplierRfqRecipients';
import SupplierRecipientPicker from './SupplierRecipientPicker';
import './TimberRfqSuppliersModal.css';

export interface TimberRfqSuppliersModalProps {
  project: RfqProject;
  projectSupplierOrgIds: string[];
  timberCapableOrgs: SupplierOrg[];
  projectSite: ProjectSiteAddressInput;
  /** CNC RFQ on the same deal — affects delivery defaults for dual-work-type suppliers. */
  cncRfqProject?: RfqProject | null;
  /**
   * Proposal structures suppliers can quote (see rfqProjectStructures). When given,
   * other enabled node sets — e.g. the pre-split whole-project default — are hidden.
   */
  rfqStructureIds?: ReadonlySet<string>;
  onClose: () => void;
  onSave: (updates: {
    supplierOrgIds: string[];
    supplierOrgId: string;
    supplierOrgName?: string;
    timberSupplierInvites: TimberSupplierInvite[];
    approvedSupplierOrgId?: string;
  }) => Promise<void>;
}

function initialDrynessByOrg(
  project: RfqProject,
  orgIds: string[],
): Record<string, Record<string, SelectableTimberRfqDrynessSpec>> {
  const out: Record<string, Record<string, SelectableTimberRfqDrynessSpec>> = {};
  for (const orgId of orgIds) {
    const entries = rfqProjectTimberSpeciesDrynessEntries(project, orgId);
    if (entries.length) {
      out[orgId] = Object.fromEntries(entries.map((entry) => [entry.species, entry.drynessSpec]));
    }
  }
  return out;
}

function initialShipToByOrg(
  project: RfqProject,
  orgIds: string[],
  orgs: SupplierOrg[],
  cncRfqProject: RfqProject | null | undefined,
): Record<string, SupplierShipToConfig> {
  const out: Record<string, SupplierShipToConfig> = {};
  for (const orgId of orgIds) {
    const org = orgs.find(o => o.id === orgId);
    if (!org) continue;
    const invite = project.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
    out[orgId] = initialShipToConfigForOrg(org, invite, {
      workType: 'timber_rfq',
      timberProject: project,
      cncProject: cncRfqProject,
    });
  }
  return out;
}

const TimberRfqSuppliersModal: React.FC<TimberRfqSuppliersModalProps> = ({
  project,
  projectSupplierOrgIds,
  timberCapableOrgs,
  projectSite,
  cncRfqProject,
  rfqStructureIds,
  onClose,
  onSave,
}) => {
  const { crmUsers, supplierInvites } = useCRM();
  const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>(() => [...projectSupplierOrgIds]);
  /** org → recipient emails; absent = everyone. */
  const [notifyEmailsByOrg, setNotifyEmailsByOrg] = useState<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.notifyEmails?.length) out[invite.supplierOrgId] = invite.notifyEmails;
    }
    return out;
  });
  const recipientCandidatesFor = (orgId: string) =>
    supplierRfqRecipientCandidates(timberCapableOrgs.find(o => o.id === orgId), supplierInvites, crmUsers);
  const [speciesByOrg, setSpeciesByOrg] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    for (const orgId of projectSupplierOrgIds) {
      initial[orgId] = rfqProjectTimberSupplierSpecies(project, orgId);
    }
    return initial;
  });
  const [drynessByOrg, setDrynessByOrg] = useState<Record<string, Record<string, SelectableTimberRfqDrynessSpec>>>(
    () => initialDrynessByOrg(project, projectSupplierOrgIds),
  );
  const [shipToByOrg, setShipToByOrg] = useState<Record<string, SupplierShipToConfig>>(() =>
    initialShipToByOrg(project, projectSupplierOrgIds, timberCapableOrgs, cncRfqProject),
  );
  const [structureScopeByOrg, setStructureScopeByOrg] = useState<Record<string, 'all' | 'selected'>>(() => {
    const out: Record<string, 'all' | 'selected'> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.structureScope === 'selected' && invite.structures?.length) {
        out[invite.supplierOrgId] = 'selected';
      }
    }
    return out;
  });
  const [structuresByOrg, setStructuresByOrg] = useState<Record<string, TimberSupplierStructureRef[]>>(() => {
    const out: Record<string, TimberSupplierStructureRef[]> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.structures?.length) out[invite.supplierOrgId] = invite.structures;
    }
    return out;
  });
  const [structureSpeciesByOrg, setStructureSpeciesByOrg] = useState<Record<string, Record<string, string[]>>>(() => {
    const out: Record<string, Record<string, string[]>> = {};
    for (const invite of project.timberSupplierInvites ?? []) {
      if (invite.structureSpecies && Object.keys(invite.structureSpecies).length) {
        out[invite.supplierOrgId] = invite.structureSpecies;
      }
    }
    return out;
  });
  const [proposalNodeSets, setProposalNodeSets] = useState<ProposalNodeSet[]>([]);
  const [proposalSpeciesSource, setProposalSpeciesSource] = useState<ProposalSpeciesSelectionSource | null>(null);
  const [proposalLoaded, setProposalLoaded] = useState(false);
  const [useProposalSpecies, setUseProposalSpecies] = useState(false);
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeToTimberItems(setTimberItems), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', project.dealId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as {
          nodeSets?: ProposalNodeSet[];
          selectedSpecies?: string;
          speciesPricing?: ProposalSpeciesSelectionSource['speciesPricing'];
        };
        const enabled = (data.nodeSets ?? []).filter(ns => ns.enabled !== false);
        const quotable = rfqStructureIds?.size ? enabled.filter(ns => rfqStructureIds.has(ns.id)) : enabled;
        setProposalNodeSets(quotable.length ? quotable : enabled);
        setProposalSpeciesSource({
          selectedSpecies: data.selectedSpecies,
          speciesPricing: data.speciesPricing,
        });
      } catch (err) {
        console.error('Could not load proposal for supplier structure scoping:', err);
      } finally {
        if (!cancelled) setProposalLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.dealId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const selectedSet = useMemo(() => new Set(selectedOrgIds), [selectedOrgIds]);

  const savedSpeciesExtras = useMemo(() => {
    const extras: string[] = [];
    for (const orgId of selectedOrgIds) {
      extras.push(...(speciesByOrg[orgId] ?? []));
      extras.push(...rfqProjectTimberSupplierSpecies(project, orgId));
    }
    return extras;
  }, [project, selectedOrgIds, speciesByOrg]);

  const speciesOptions = useMemo(
    () => allSelectableTimberSpecies(timberItems, savedSpeciesExtras),
    [timberItems, savedSpeciesExtras],
  );

  const filteredOrgs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const sorted = [...timberCapableOrgs].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    if (!q) return sorted;
    return sorted.filter((org) => org.name.toLowerCase().includes(q));
  }, [filter, timberCapableOrgs]);

  const defaultDrynessForSpecies = (orgId: string, species: string): SelectableTimberRfqDrynessSpec =>
    rfqProjectTimberSpeciesDryness(project, orgId, species);

  const toggleSupplier = (orgId: string, checked: boolean) => {
    setError(null);
    if (checked) {
      setSelectedOrgIds((prev) => (prev.includes(orgId) ? prev : [...prev, orgId]));
      const orgName = timberCapableOrgs.find((o) => o.id === orgId)?.name ?? '';
      const catalog = speciesProvidedBySupplier(orgName, timberItems);
      setSpeciesByOrg((prev) => {
        if (prev[orgId]?.length || !catalog.length) return prev;
        return { ...prev, [orgId]: catalog };
      });
      if (catalog.length) {
        setDrynessByOrg((prev) => {
          if (prev[orgId] && Object.keys(prev[orgId]).length) return prev;
          return {
            ...prev,
            [orgId]: Object.fromEntries(catalog.map((sp) => [sp, defaultDrynessForSpecies(orgId, sp)])),
          };
        });
      }
      const org = timberCapableOrgs.find(o => o.id === orgId);
      if (org) {
        setShipToByOrg(prev =>
          prev[orgId]
            ? prev
            : {
                ...prev,
                [orgId]: defaultShipToConfigForOrg(org, {
                  workType: 'timber_rfq',
                  timberProject: project,
                  cncProject: cncRfqProject,
                }),
              },
        );
      }
      return;
    }
    setSelectedOrgIds((prev) => prev.filter((id) => id !== orgId));
    setDrynessByOrg((prev) => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
    setShipToByOrg(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
  };

  const toggleSpecies = (orgId: string, species: string) => {
    setError(null);
    setSpeciesByOrg((prev) => {
      const current = new Set(prev[orgId] ?? []);
      const wasOn = current.has(species);
      if (wasOn) current.delete(species);
      else current.add(species);
      setDrynessByOrg((dryPrev) => {
        const orgDry = { ...(dryPrev[orgId] ?? {}) };
        if (wasOn) {
          delete orgDry[species];
        } else {
          orgDry[species] = dryPrev[orgId]?.[species] ?? defaultDrynessForSpecies(orgId, species);
        }
        return { ...dryPrev, [orgId]: orgDry };
      });
      return {
        ...prev,
        [orgId]: Array.from(current).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
      };
    });
  };

  const setSpeciesDryness = (orgId: string, species: string, dryness: TimberRfqDrynessSpec) => {
    setError(null);
    setDrynessByOrg((prev) => ({
      ...prev,
      [orgId]: {
        ...(prev[orgId] ?? {}),
        [species]: dryness === 'green' ? 'green' : 'kiln_dried',
      },
    }));
  };

  const structureRefMatchesNodeSet = (ref: TimberSupplierStructureRef, ns: ProposalNodeSet): boolean =>
    ref.id === ns.id
    || (!!ref.name.trim() && ref.name.trim().toLowerCase() === ns.name.trim().toLowerCase());

  const setStructureScope = (orgId: string, scope: 'all' | 'selected') => {
    setError(null);
    setStructureScopeByOrg(prev => ({ ...prev, [orgId]: scope }));
  };

  const toggleStructure = (orgId: string, ns: ProposalNodeSet) => {
    setError(null);
    setStructuresByOrg(prev => {
      const current = prev[orgId] ?? [];
      const has = current.some(ref => structureRefMatchesNodeSet(ref, ns));
      const next = has
        ? current.filter(ref => !structureRefMatchesNodeSet(ref, ns))
        : [...current, { id: ns.id, name: ns.name }];
      return { ...prev, [orgId]: next };
    });
  };

  /** Structures this supplier currently quotes (scope-filtered proposal node sets). */
  const structuresInScope = (orgId: string): ProposalNodeSet[] => {
    if (structureScopeByOrg[orgId] !== 'selected') return proposalNodeSets;
    const refs = structuresByOrg[orgId] ?? [];
    if (!refs.length) return proposalNodeSets;
    return proposalNodeSets.filter(ns => refs.some(ref => structureRefMatchesNodeSet(ref, ns)));
  };

  /** Species this supplier prices on one structure (absent map entry = every requested species). */
  const speciesOnStructure = (orgId: string, ns: ProposalNodeSet): string[] => {
    const requested = speciesByOrg[orgId] ?? [];
    const map = structureSpeciesByOrg[orgId];
    const key = map
      ? Object.keys(map).find(id => id === ns.id || id.trim().toLowerCase() === ns.name.trim().toLowerCase())
      : undefined;
    if (!map || !key) return requested;
    return map[key].filter(sp => requested.some(r => r.toLowerCase() === sp.toLowerCase()));
  };

  const toggleStructureSpecies = (orgId: string, ns: ProposalNodeSet, species: string) => {
    setError(null);
    const requested = speciesByOrg[orgId] ?? [];
    const current = speciesOnStructure(orgId, ns);
    const next = current.some(sp => sp.toLowerCase() === species.toLowerCase())
      ? current.filter(sp => sp.toLowerCase() !== species.toLowerCase())
      : requested.filter(sp => current.includes(sp) || sp === species);
    setStructureSpeciesByOrg(prev => {
      const map = { ...(prev[orgId] ?? {}) };
      // Re-key by the proposal structure id so hub-CSV name matches still resolve.
      for (const id of Object.keys(map)) {
        if (id !== ns.id && id.trim().toLowerCase() === ns.name.trim().toLowerCase()) delete map[id];
      }
      if (next.length === requested.length) delete map[ns.id];
      else map[ns.id] = next;
      return { ...prev, [orgId]: map };
    });
  };

  // While the toggle is on, species + dryness stay synced to the proposal for
  // every selected supplier, tracking structure-scope changes live.
  useEffect(() => {
    if (!useProposalSpecies || !proposalNodeSets.length) return;
    const speciesUpdates: Record<string, string[]> = {};
    const drynessUpdates: Record<string, Record<string, SelectableTimberRfqDrynessSpec>> = {};
    for (const orgId of selectedOrgIds) {
      const scope =
        structureScopeByOrg[orgId] === 'selected'
          ? { structureScope: 'selected' as const, structures: structuresByOrg[orgId] ?? [] }
          : undefined;
      const entries = proposalSpeciesDrynessForInvite(proposalSpeciesSource, proposalNodeSets, scope);
      if (!entries.length) continue;
      speciesUpdates[orgId] = entries.map(entry => entry.species);
      drynessUpdates[orgId] = Object.fromEntries(
        entries.map(entry => [entry.species, entry.drynessSpec]),
      );
    }
    if (!Object.keys(speciesUpdates).length) return;
    setSpeciesByOrg(prev => ({ ...prev, ...speciesUpdates }));
    setDrynessByOrg(prev => ({ ...prev, ...drynessUpdates }));
  }, [
    useProposalSpecies,
    selectedOrgIds,
    structureScopeByOrg,
    structuresByOrg,
    proposalNodeSets,
    proposalSpeciesSource,
  ]);

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (!selectedOrgIds.length) {
        await onSave({
          supplierOrgIds: [],
          supplierOrgId: '',
          timberSupplierInvites: [],
          approvedSupplierOrgId: undefined,
        });
        onClose();
        return;
      }
      const timberSupplierInvites = buildTimberSupplierInvites(
        selectedOrgIds,
        speciesByOrg,
        project.timberSupplierInvites,
        drynessByOrg,
        project.timberDrynessSpec,
        Object.fromEntries(
          selectedOrgIds.flatMap(orgId => {
            const org = timberCapableOrgs.find(o => o.id === orgId);
            const config =
              shipToByOrg[orgId]
              ?? (org
                ? defaultShipToConfigForOrg(org, {
                    workType: 'timber_rfq',
                    timberProject: project,
                    cncProject: cncRfqProject,
                  })
                : undefined);
            return config ? [[orgId, config]] : [];
          }),
        ),
        Object.fromEntries(
          selectedOrgIds.map(orgId => [
            orgId,
            structureScopeByOrg[orgId] === 'selected'
              ? { scope: 'selected' as const, structures: structuresByOrg[orgId] ?? [] }
              : { scope: 'all' as const, structures: [] },
          ]),
        ),
        undefined,
        Object.fromEntries(selectedOrgIds.map(orgId => [orgId, structureSpeciesByOrg[orgId] ?? null])),
        Object.fromEntries(
          selectedOrgIds.map(orgId => [
            orgId,
            normalizeNotifyEmails(notifyEmailsByOrg[orgId] ?? null, recipientCandidatesFor(orgId)) ?? null,
          ]),
        ),
      );
      const primaryId = selectedOrgIds[0];
      const primaryName = timberCapableOrgs.find((o) => o.id === primaryId)?.name;
      const approvedId = project.approvedSupplierOrgId;
      await onSave({
        supplierOrgIds: selectedOrgIds,
        supplierOrgId: primaryId,
        supplierOrgName: primaryName,
        timberSupplierInvites,
        approvedSupplierOrgId:
          approvedId && selectedOrgIds.includes(approvedId) ? approvedId : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save suppliers.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="ops-modal-overlay srfq-timber-suppliers-modal-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="ops-modal srfq-timber-suppliers-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="srfq-timber-suppliers-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ops-modal-header srfq-timber-suppliers-modal-header">
          <div className="srfq-timber-suppliers-modal-title-block">
            <Users size={16} aria-hidden className="srfq-timber-suppliers-modal-icon" />
            <h3 id="srfq-timber-suppliers-modal-title">Timber suppliers</h3>
          </div>
          <button
            type="button"
            className="ops-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body srfq-timber-suppliers-modal-body">
          <p className="srfq-timber-suppliers-modal-lead">
            Choose suppliers for this timber RFQ and specify which species each should quote, including dryness per
            species and where materials should be delivered.
          </p>

          <div className="srfq-timber-suppliers-modal-toolbar">
            <input
              type="search"
              className="ops-modal-input"
              placeholder="Filter suppliers…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              role="switch"
              aria-checked={useProposalSpecies}
              className={`srfq-timber-suppliers-modal-proposal-species-toggle${useProposalSpecies ? ' on' : ''}`}
              disabled={submitting || !proposalLoaded || !proposalNodeSets.length}
              title="Sync each supplier's requested species and dryness (KD vs GRN) from the proposal — the client's selected species when chosen, otherwise the species on their assigned structures."
              onClick={() => setUseProposalSpecies(v => !v)}
            >
              <Wand2 size={13} aria-hidden />
              Use proposal species
              <span className="srfq-timber-suppliers-modal-proposal-species-track" aria-hidden>
                <span className="srfq-timber-suppliers-modal-proposal-species-thumb" />
              </span>
            </button>
          </div>
          {useProposalSpecies && (
            <p className="srfq-timber-suppliers-modal-proposal-species-hint">
              Species and dryness are synced from the proposal (including per-structure KD/GRN). Adjust each
              supplier's structures to change what they quote, or turn the toggle off to edit manually.
            </p>
          )}

          <div className="srfq-timber-suppliers-modal-list" role="list">
            {filteredOrgs.length === 0 ? (
              <p className="srfq-muted srfq-timber-suppliers-modal-empty">No matching suppliers.</p>
            ) : (
              filteredOrgs.map((org) => {
                if (!org.id) return null;
                const checked = selectedSet.has(org.id);
                const requested = speciesByOrg[org.id] ?? [];
                const catalog = speciesProvidedBySupplier(org.name, timberItems);
                const mixedDrynessSpecies =
                  checked && useProposalSpecies
                    ? proposalMixedDrynessSpecies(
                        proposalSpeciesSource,
                        proposalNodeSets,
                        structureScopeByOrg[org.id] === 'selected'
                          ? { structureScope: 'selected', structures: structuresByOrg[org.id] ?? [] }
                          : undefined,
                      )
                    : [];

                return (
                  <div
                    key={org.id}
                    className={`srfq-timber-suppliers-modal-row ${checked ? 'selected' : ''}`}
                    role="listitem"
                  >
                    <label className="srfq-timber-suppliers-modal-supplier">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={submitting}
                        onChange={(e) => toggleSupplier(org.id!, e.target.checked)}
                      />
                      <span className="srfq-timber-suppliers-modal-supplier-name">{org.name}</span>
                    </label>

                    {checked && proposalNodeSets.length > 0 && (
                      <div className="srfq-timber-suppliers-modal-structures">
                        <span className="srfq-timber-suppliers-modal-species-label">Structures to quote</span>
                        <div
                          className="srfq-timber-suppliers-modal-structure-toggle"
                          role="radiogroup"
                          aria-label={`Structure scope for ${org.name}`}
                        >
                          <button
                            type="button"
                            role="radio"
                            aria-checked={structureScopeByOrg[org.id] !== 'selected'}
                            className={`srfq-timber-suppliers-modal-scope-btn${
                              structureScopeByOrg[org.id] !== 'selected' ? ' on' : ''
                            }`}
                            disabled={submitting}
                            onClick={() => setStructureScope(org.id!, 'all')}
                          >
                            All structures
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={structureScopeByOrg[org.id] === 'selected'}
                            className={`srfq-timber-suppliers-modal-scope-btn${
                              structureScopeByOrg[org.id] === 'selected' ? ' on' : ''
                            }`}
                            disabled={submitting}
                            onClick={() => setStructureScope(org.id!, 'selected')}
                          >
                            Selected structures
                          </button>
                        </div>
                        {structureScopeByOrg[org.id] === 'selected' && (
                          <>
                            <ul className="srfq-timber-suppliers-modal-structure-options">
                              {proposalNodeSets.map(ns => {
                                const isOn = (structuresByOrg[org.id!] ?? []).some(ref =>
                                  structureRefMatchesNodeSet(ref, ns),
                                );
                                return (
                                  <li key={ns.id}>
                                    <label
                                      className={`srfq-timber-suppliers-modal-species-chip ${isOn ? 'on' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isOn}
                                        disabled={submitting}
                                        onChange={() => toggleStructure(org.id!, ns)}
                                      />
                                      <span>{ns.name}</span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                            {(structuresByOrg[org.id!] ?? []).length === 0 && (
                              <p className="srfq-timber-suppliers-modal-catalog-hint">
                                No structures selected — this supplier will receive all structures until at
                                least one is chosen.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {checked && (
                      <div className="srfq-timber-suppliers-modal-species">
                        <span className="srfq-timber-suppliers-modal-species-label">Requested species</span>
                        <ul className="srfq-timber-suppliers-modal-species-options">
                          {speciesOptions.map((species) => {
                            const isOn = requested.includes(species);
                            const inCatalog = catalog.some(
                              (c) => c.localeCompare(species, undefined, { sensitivity: 'base' }) === 0,
                            );
                            return (
                              <li key={species}>
                                <label
                                  className={`srfq-timber-suppliers-modal-species-chip ${isOn ? 'on' : ''} ${inCatalog ? 'catalog' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isOn}
                                    disabled={submitting || useProposalSpecies}
                                    onChange={() => toggleSpecies(org.id!, species)}
                                  />
                                  <span>{species}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>

                        {requested.length > 1 && structuresInScope(org.id!).length > 1 && (
                          <div className="srfq-timber-suppliers-modal-structure-species">
                            <span className="srfq-timber-suppliers-modal-species-label">Species by structure</span>
                            <p className="srfq-timber-suppliers-modal-catalog-hint srfq-timber-suppliers-modal-structure-species-hint">
                              Untick a species on a structure to leave it out of that structure's list. Every
                              species is priced on every structure unless you narrow it here.
                            </p>
                            <table className="srfq-timber-suppliers-modal-structure-species-table">
                              <thead>
                                <tr>
                                  <th scope="col">Structure</th>
                                  {requested.map(species => (
                                    <th key={species} scope="col">{species}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {structuresInScope(org.id!).map(ns => {
                                  const on = speciesOnStructure(org.id!, ns);
                                  return (
                                    <tr key={ns.id} className={on.length === 0 ? 'none' : ''}>
                                      <th scope="row">
                                        {ns.name}
                                        {on.length === 0 && (
                                          <span className="srfq-timber-suppliers-modal-structure-species-none">
                                            not priced
                                          </span>
                                        )}
                                      </th>
                                      {requested.map(species => {
                                        const isOn = on.some(sp => sp.toLowerCase() === species.toLowerCase());
                                        return (
                                          <td key={species}>
                                            <input
                                              type="checkbox"
                                              checked={isOn}
                                              disabled={submitting}
                                              aria-label={`${species} on ${ns.name}`}
                                              onChange={() => toggleStructureSpecies(org.id!, ns, species)}
                                            />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {requested.length > 0 && (
                          <div className="srfq-timber-suppliers-modal-dryness">
                            <span className="srfq-timber-suppliers-modal-species-label">Dryness per species</span>
                            <ul className="srfq-timber-suppliers-modal-dryness-list">
                              {requested.map((species) => (
                                <li key={species} className="srfq-timber-suppliers-modal-dryness-row">
                                  <span className="srfq-timber-suppliers-modal-dryness-species">{species}</span>
                                  <select
                                    className="srfq-timber-suppliers-modal-dryness-select ops-modal-input"
                                    value={drynessByOrg[org.id!]?.[species] ?? defaultDrynessForSpecies(org.id!, species)}
                                    disabled={submitting || useProposalSpecies}
                                    aria-label={`Dryness for ${species}`}
                                    onChange={(e) =>
                                      setSpeciesDryness(org.id!, species, e.target.value as TimberRfqDrynessSpec)
                                    }
                                  >
                                    <option value="green">Green</option>
                                    <option value="kiln_dried">Kiln-Dried</option>
                                  </select>
                                </li>
                              ))}
                            </ul>
                            {mixedDrynessSpecies.length > 0 && (
                              <p className="srfq-timber-suppliers-modal-mixed-dryness-hint">
                                {mixedDrynessSpecies.join(', ')}{' '}
                                {mixedDrynessSpecies.length === 1 ? 'is' : 'are'} mixed KD/GRN on the
                                proposal — each timber in the package keeps its own dryness.
                              </p>
                            )}
                          </div>
                        )}

                        {catalog.length > 0 && (
                          <p className="srfq-timber-suppliers-modal-catalog-hint">
                            In materials database: {catalog.join(', ')}
                          </p>
                        )}

                        <SupplierShipToFields
                          org={org}
                          projectSite={projectSite}
                          workType="timber_rfq"
                          timberProject={project}
                          cncProject={cncRfqProject}
                          config={
                            shipToByOrg[org.id]
                            ?? defaultShipToConfigForOrg(org, {
                              workType: 'timber_rfq',
                              timberProject: project,
                              cncProject: cncRfqProject,
                            })
                          }
                          disabled={submitting}
                          onChange={next =>
                            setShipToByOrg(prev => ({
                              ...prev,
                              [org.id!]: next,
                            }))
                          }
                        />

                        <div className="srfq-timber-suppliers-modal-recipients">
                          <SupplierRecipientPicker
                            orgName={org.name}
                            candidates={recipientCandidatesFor(org.id!)}
                            selected={notifyEmailsByOrg[org.id!] ?? null}
                            disabled={submitting}
                            onChange={next => setNotifyEmailsByOrg(prev => ({ ...prev, [org.id!]: next }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {error && (
            <div className="srfq-timber-suppliers-modal-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </div>
          )}
        </div>

        <footer className="ops-modal-footer">
          <button
            type="button"
            className="ops-hide-completed-btn"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ops-new-btn"
            onClick={() => void handleSave()}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="srfq-timber-suppliers-modal-spin" aria-hidden /> Saving…
              </>
            ) : (
              'Save suppliers'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default TimberRfqSuppliersModal;
